// ============================================================
// MongoDBBsonJson.jsx
//
// A branded, technical deep-dive into BSON (Binary JSON) and
// its relationship with standard JSON.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #00ED64  (MongoDB Spring Green)
//   Accent      → #00E0FF  (Cyan / Binary)
//   Text        → #FFFFFF  (White)
//   Code        → #010409  (GitHub Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Tooltip,
} from "@mui/material";

/* MUI Icons */
import DataObjectIcon from "@mui/icons-material/DataObject";
import MemoryIcon from "@mui/icons-material/Memory";
import TerminalIcon from "@mui/icons-material/Terminal";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import StorageIcon from "@mui/icons-material/Storage";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 237, 100, 0.2)",
    borderHover: "rgba(0, 224, 255, 0.5)",
    green: "#00ED64",
    cyan: "#00E0FF",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeBlock({ code, label, color = BRAND.green }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#D1D5DB", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

function DataTypeChip({ label, type }) {
    return (
        <Tooltip title={`BSON Type: ${type}`} arrow>
            <Chip
                label={label}
                sx={{
                    bgcolor: "rgba(0, 224, 255, 0.05)",
                    color: BRAND.cyan,
                    border: "1px solid rgba(0, 224, 255, 0.2)",
                    m: 0.5,
                    fontWeight: 600,
                    fontSize: "0.75rem"
                }}
            />
        </Tooltip>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBBsonJson() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.cyan }} />}
                        label="Data Anatomy · Module 02"
                        sx={{ bgcolor: "rgba(0,224,255,0.1)", color: BRAND.cyan, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        BSON vs. <span style={{ color: BRAND.green }}>JSON</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        MongoDB stores data as <strong>BSON</strong> (Binary JSON). While you interact with JSON in the shell and your apps,
                        BSON provides the speed and specialized data types needed for high-performance database operations.
                    </Typography>
                </Box>

                {/* 2. THE EVOLUTION OF DATA */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <MemoryIcon sx={{ color: BRAND.cyan }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Why BSON? (Binary JSON)</Typography>
                    </Box>

                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4, lineHeight: 1.8 }}>
                        JSON is great for data exchange because it is human-readable, but it is slow for databases to parse.
                        BSON was designed to be <strong>lightweight</strong>, <strong>traversable</strong>, and <strong>efficient</strong>.
                        It adds support for data types that JSON lacks, such as Dates and Binary data.
                    </Typography>



                    <Grid container spacing={3} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 4, border: `1px solid ${BRAND.border}`, height: '100%' }}>
                                <Typography variant="h6" sx={{ color: BRAND.green, mb: 1, fontWeight: 700 }}>Speed</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    BSON documents include "length" prefixes, allowing MongoDB to skip over fields it doesn't need without reading the whole document.
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 4, border: `1px solid ${BRAND.border}`, height: '100%' }}>
                                <Typography variant="h6" sx={{ color: BRAND.cyan, mb: 1, fontWeight: 700 }}>Rich Types</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    JSON only has numbers, strings, booleans, arrays, and objects. BSON adds Int32, Int64, Decimal128, Date, and ObjectId.
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 4, border: `1px solid ${BRAND.border}`, height: '100%' }}>
                                <Typography variant="h6" sx={{ color: BRAND.white, mb: 1, fontWeight: 700 }}>The _id Field</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    Every BSON document requires a unique <code>_id</code>. By default, MongoDB uses a 12-byte <strong>ObjectId</strong>.
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. COMPARISON CODE EXAMPLES */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <IntegrationInstructionsIcon sx={{ color: BRAND.green }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Standard JSON vs. Extended JSON (BSON)</Typography>
                    </Box>

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" sx={{ color: BRAND.muted, mb: 2, fontWeight: 700 }}>Standard JSON (Limited Types)</Typography>
                            <CodeTerminal
                                label="client_side.json"
                                code={`{\n  "name": "Gemini",\n  "isActive": true,\n  "score": 95.5,\n  "lastLogin": "2026-04-04T22:20:00Z" \n  // Note: lastLogin is just a string here!\n}`}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" sx={{ color: BRAND.green, mb: 2, fontWeight: 700 }}>BSON / Extended JSON (Rich Types)</Typography>
                            <CodeTerminal
                                label="mongodb_internal.bson"
                                color={BRAND.cyan}
                                code={`{\n  "_id": ObjectId("65f1a..."),\n  "name": "Gemini",\n  "isActive": true,\n  "score": { "$numberDouble": "95.5" },\n  "lastLogin": { "$date": "2026-04-04T22:20:00Z" }\n  // Note: MongoDB treats this as a Date object!\n}`}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* 4. THE OBJECT ID ANATOMY */}
                <Box sx={{ mb: 10, p: 4, borderRadius: 6, bgcolor: "rgba(0, 224, 255, 0.03)", border: `1px solid ${BRAND.border}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <StorageIcon sx={{ color: BRAND.cyan }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Anatomy of an ObjectId</Typography>
                    </Box>

                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
                        The <code>ObjectId</code> is a 12-byte identifier generated quickly by the driver/server. Because it contains a timestamp,
                        documents are naturally sorted by creation time if no other index is used.
                    </Typography>



                    <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <DataTypeChip label="4-byte Timestamp" type="Seconds since epoch" />
                        <DataTypeChip label="5-byte Random" type="Per-process unique value" />
                        <DataTypeChip label="3-byte Counter" type="Incrementing starting value" />
                    </Box>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #00684A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <DataObjectIcon style={{ fontSize: 40, color: BRAND.green, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>BSON Concept Mastered?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>Now that you understand how data is stored, let's learn how to install the environment.</Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Chip label="Install MongoDB" onClick={() => {}} sx={{ bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                        <Chip label="BSON Spec Docs" variant="outlined" onClick={() => {}} sx={{ borderColor: BRAND.muted, color: BRAND.white }} />
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}

/* ── Junior Dev Alias ── */
const CodeTerminal = CodeBlock;
